import { type PointTooltipProps } from '@nivo/line'
import { Card, Divider, Stack, styled } from '@mui/material'
import { ContentChip, decimalToLocalizedString, isoDateStringToLocalFormat, RectangleIcon, tokens } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { type LineChartSeries } from 'ComponentLibrary/src/molecules/Chart/types'
import { type WithBars } from './types'
import { LineChartTooltip, type LineChartTooltipTranslations } from 'ComponentLibrary/src/molecules/Chart/LineChartTooltip'

type IncomeChartTooltipProps = PointTooltipProps<LineChartSeries> & {
    translations?: LineChartTooltipTranslations
    locale?: string
}

export function IncomeChartTooltip({ point, translations, locale = 'de-DE' }: IncomeChartTooltipProps) {
    const bars = (point.data.bars ?? []) as WithBars<object>['bars']

    if (!bars.length) {
        return <LineChartTooltip point={point} translations={translations} locale={locale} />
    }

    return (
        <StyledTooltip>
            <Stack direction="column">
                <Stack direction="row" gap={tokens.SizingSpacing050} alignItems="baseline">
                    <ColoredBox $color={point.seriesColor}>
                        <RectangleIcon height={tokens.SizingIconS} width={tokens.SizingIconS} />
                    </ColoredBox>
                    <Typography variant="textXS" color={tokens.ColorTextDefault}>
                        {point.seriesId}:
                    </Typography>
                    <Typography variant="textXS">{point.data.yFormatted}</Typography>
                </Stack>
                {bars.toReversed().map(({ id, color, value, unit }) => (
                    <Stack key={id} direction="row" gap={tokens.SizingSpacing050} alignItems="baseline">
                        <ColoredBox $color={color}>
                            <RectangleIcon height={tokens.SizingIconS} width={tokens.SizingIconS} />
                        </ColoredBox>
                        <Typography variant="textXS" color={tokens.ColorTextDefault}>
                            {id}:
                        </Typography>
                        <Typography variant="textXS">{`${decimalToLocalizedString(value, false, 0, undefined, locale)}${unit}`}</Typography>
                    </Stack>
                ))}
            </Stack>
            <Divider />
            <Stack direction="row" gap={tokens.SizingSpacing050} alignItems="baseline">
                {point.data.isPrediction && <ContentChip color="orange" size="small" label={translations?.draftLabel ?? 'Entwurf'} />}
                <Typography variant="textXS" color={tokens.ColorTextDefault}>
                    {`${translations?.dateLabel ?? 'Datum'}:`}
                </Typography>
                <Typography variant="textXS">{isoDateStringToLocalFormat(point.data.date, false, locale)}</Typography>
            </Stack>
        </StyledTooltip>
    )
}

const StyledTooltip = styled(Card)(() => ({
    background: tokens.ColorSurfaceDefault,
    padding: '6px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.SizingSpacing050,
    lineHeight: '1.4',
}))

const ColoredBox = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})(({ $color }: { $color: string }) => ({
    transform: 'translateY(3px)',
    color: $color,
}))
